import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FileText, FileSignature, Download, ShieldCheck, Search, FolderLock, Trash2, Loader2, UploadCloud, FolderOpen } from "lucide-react";
import { EmptyState, openOnboarding } from "../../components/onboarding/EmptyState";
import { EMPTY as EMPTY_TXT } from "../../content/onboardingTexts";
import api from "../../lib/api";
import { toast } from "sonner";

const RECLASS_CATS = ["Bail", "Diagnostic", "DPE", "Assurance", "État des lieux", "Autre"];

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const imgSrc = (url) => (url && url.startsWith("/api") ? `${BACKEND_URL}${url}` : url);

const FILTERS = ["Tous", "Baux signés", "Bail", "Diagnostic", "DPE", "État des lieux", "Assurance", "Autre"];

export default function Coffre() {
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("Tous");
  const [propFilter, setPropFilter] = useState("Tous");
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState([]);
  const [confirmDel, setConfirmDel] = useState(null); // "bulk" | docId | null
  const [delBusy, setDelBusy] = useState(false);
  const [properties, setProperties] = useState([]);
  const [uploadProp, setUploadProp] = useState("");
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const navigate = useNavigate();

  const loadVault = () => api.get("/documents/vault").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => {
    loadVault();
    api.get("/properties").then((r) => setProperties(r.data || [])).catch(() => {});
  }, []);

  const handleFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    if (!uploadProp) { toast.error("Choisissez d'abord le bien concerné"); return; }
    setUploading(true);
    let ok = 0;
    for (const file of files) {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("category", "Autre");
      try {
        await api.post(`/properties/${uploadProp}/documents`, fd);
        ok += 1;
      } catch (e) {
        toast.error(`${file.name} : ${e?.response?.data?.detail || "échec de l'import"}`);
      }
    }
    if (ok > 0) { toast.success(`${ok} document(s) importé(s)`); await loadVault(); }
    setUploading(false);
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    handleFiles(e.dataTransfer.files);
  };

  const reclassify = async (docId, category) => {
    try {
      await api.patch(`/properties/documents/${docId}/category`, { category });
      setData((prev) => ({ ...prev, documents: (prev.documents || []).map((d) => d.id === docId ? { ...d, category, auto_classified: false } : d) }));
      toast.success(`Document reclassé dans « ${category} »`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Reclassement impossible"); }
  };

  const setExpiry = async (docId, expiry_date) => {
    try {
      await api.patch(`/properties/documents/${docId}/expiry`, { expiry_date });
      setData((prev) => ({ ...prev, documents: (prev.documents || []).map((d) => d.id === docId ? { ...d, expiry_date: expiry_date || null, expiry_auto: false } : d) }));
      toast.success(expiry_date ? "Date de validité mise à jour" : "Date de validité retirée");
    } catch (e) { toast.error(e?.response?.data?.detail || "Mise à jour impossible"); }
  };

  const toggleSel = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const doDelete = async () => {
    setDelBusy(true);
    try {
      let removed;
      if (confirmDel === "bulk") {
        const res = await api.post("/properties/documents/bulk-delete", { ids: selected });
        removed = new Set(selected);
        toast.success(`${res.data.deleted} document(s) supprimé(s)`);
        setSelected([]);
      } else {
        await api.delete(`/properties/documents/${confirmDel}`);
        removed = new Set([confirmDel]);
        toast.success("Document supprimé");
        setSelected((s) => s.filter((x) => x !== confirmDel));
      }
      setData((prev) => ({ ...prev, documents: (prev.documents || []).filter((d) => !removed.has(d.id)) }));
      setConfirmDel(null);
    } catch (e) { toast.error(e?.response?.data?.detail || "Suppression impossible"); }
    finally { setDelBusy(false); }
  };

  if (!data) return <div className="py-20 text-center text-brand-green/50">Chargement...</div>;

  const propNames = Array.from(new Set([
    ...(data.documents || []).map((d) => d.property_name).filter(Boolean),
    ...(data.leases || []).map((l) => l.property_name).filter(Boolean),
  ])).sort();

  const showLeases = filter === "Tous" || filter === "Baux signés";
  const docs = (data.documents || []).filter((d) => {
    const catMatch = filter === "Tous" || (filter !== "Baux signés" && (d.category === filter || (filter === "Autre" && !FILTERS.includes(d.category))));
    const propMatch = propFilter === "Tous" || d.property_name === propFilter;
    const qMatch = (d.label || "").toLowerCase().includes(q.toLowerCase()) || (d.property_name || "").toLowerCase().includes(q.toLowerCase());
    return catMatch && propMatch && qMatch;
  });
  const leases = showLeases ? (data.leases || []).filter((l) =>
    (propFilter === "Tous" || l.property_name === propFilter) &&
    ((l.property_name || "").toLowerCase().includes(q.toLowerCase()) || (l.signer_name || "").toLowerCase().includes(q.toLowerCase()))
  ) : [];

  const total = (data.documents?.length || 0) + (data.leases?.length || 0);

  const vaultEmpty = data && !(data.documents || []).length && !(data.leases || []).length;
  if (vaultEmpty && !properties.length) return <EmptyState icon={FolderOpen} title={EMPTY_TXT.needProperty} cta={EMPTY_TXT.locatairesNoProp.cta} onCta={() => openOnboarding(1)} testid="empty-documents-noprop" />;
  return (
    <div className="space-y-6">
      {vaultEmpty && <EmptyState icon={FolderOpen} title={EMPTY_TXT.documents.title} cta={EMPTY_TXT.documents.cta} onCta={() => document.querySelector('[data-testid="vault-upload-input"]')?.closest("label,div")?.scrollIntoView({ behavior: "smooth", block: "center" })} testid="empty-documents" />}
      <div className="flex flex-col sm:flex-row gap-4 justify-between items-start sm:items-center">
        <div className="flex items-center gap-3">
          <span className="w-11 h-11 rounded-2xl bg-brand-greenDeep text-brand-lime flex items-center justify-center"><FolderLock className="w-5 h-5" /></span>
          <div>
            <h2 className="font-display text-xl font-semibold text-brand-greenDeep">Coffre-fort documentaire</h2>
            <p className="text-sm text-brand-green/50">{total} document{total > 1 ? "s" : ""} · baux signés & diagnostics</p>
          </div>
        </div>
        <div className="flex items-center gap-2 w-full sm:w-auto">
          {selected.length > 0 && (
            <button onClick={() => setConfirmDel("bulk")} data-testid="vault-bulk-delete-btn" className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-red-50 hover:bg-red-100 text-red-600 font-semibold text-sm transition-colors whitespace-nowrap">
              <Trash2 className="w-4 h-4" /> Supprimer ({selected.length})
            </button>
          )}
          <select value={propFilter} onChange={(e) => setPropFilter(e.target.value)} className="px-4 py-2 rounded-full bg-white border border-brand-soft text-sm text-brand-green outline-none focus:border-brand-lime" data-testid="vault-property-filter">
            <option value="Tous">Tous les biens</option>
            {propNames.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
          <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-brand-soft flex-1 sm:w-64">
            <Search className="w-4 h-4 text-brand-green/40" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher..." className="bg-transparent outline-none text-sm w-full" data-testid="vault-search" />
          </div>
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        {FILTERS.map((f) => (
          <button key={f} onClick={() => setFilter(f)} data-testid={`vault-filter-${f}`}
            className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${filter === f ? "bg-brand-lime text-brand-greenDeep" : "bg-white text-brand-green/60 border border-brand-soft hover:bg-brand-softer"}`}>
            {f}
          </button>
        ))}
      </div>

      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        data-testid="vault-dropzone"
        className={`rounded-2xl border-2 border-dashed p-6 transition-colors ${dragOver ? "border-brand-limeDark bg-brand-lime/10" : "border-brand-soft bg-white"}`}
      >
        <div className="flex flex-col sm:flex-row items-center gap-4 justify-between">
          <div className="flex items-center gap-3">
            <span className="w-11 h-11 rounded-xl bg-brand-softer flex items-center justify-center text-brand-green shrink-0">
              {uploading ? <Loader2 className="w-5 h-5 animate-spin" /> : <UploadCloud className="w-5 h-5" />}
            </span>
            <div>
              <p className="text-sm font-semibold text-brand-greenDeep">Importer des documents</p>
              <p className="text-xs text-brand-green/50">Glissez-déposez vos fichiers ici (PDF, images, Word · max 15 Mo). La catégorie est détectée automatiquement.</p>
            </div>
          </div>
          <div className="flex items-center gap-2 w-full sm:w-auto">
            <select value={uploadProp} onChange={(e) => setUploadProp(e.target.value)} className="px-4 py-2 rounded-full bg-white border border-brand-soft text-sm text-brand-green outline-none focus:border-brand-lime flex-1 sm:flex-none" data-testid="vault-upload-property">
              <option value="">Choisir le bien…</option>
              {properties.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
            <label className={`inline-flex items-center gap-2 px-4 py-2 rounded-full font-semibold text-sm cursor-pointer transition-colors whitespace-nowrap ${uploadProp && !uploading ? "bg-brand-lime hover:bg-brand-limeDark text-brand-greenDeep" : "bg-brand-soft text-brand-green/40 cursor-not-allowed"}`} data-testid="vault-upload-btn">
              <UploadCloud className="w-4 h-4" /> Parcourir
              <input type="file" multiple className="hidden" disabled={!uploadProp || uploading} onChange={(e) => { handleFiles(e.target.files); e.target.value = ""; }} data-testid="vault-upload-input" />
            </label>
          </div>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {leases.map((l) => (
          <a key={l.id} href={imgSrc(l.url)} target="_blank" rel="noreferrer" className="bg-white rounded-2xl p-5 border border-brand-soft hover:shadow-lg transition-all flex items-start gap-3" data-testid={`vault-lease-${l.id}`}>
            <span className="w-11 h-11 rounded-xl bg-brand-lime/30 flex items-center justify-center text-brand-greenDeep shrink-0"><FileSignature className="w-5 h-5" /></span>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-brand-greenDeep truncate">Bail signé — {l.property_name}</p>
              <p className="text-xs text-brand-green/50">Signé par {l.signer_name} · {l.signed_at}</p>
              <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-brand-lime/30 text-brand-greenDeep font-semibold mt-1.5"><ShieldCheck className="w-3 h-3" /> Signé électroniquement</span>
            </div>
            <Download className="w-4 h-4 text-brand-green/40 shrink-0" />
          </a>
        ))}
        {docs.map((d) => (
          <div key={d.id} className={`bg-white rounded-2xl p-5 border transition-all flex items-start gap-3 ${selected.includes(d.id) ? "border-red-300 ring-1 ring-red-200" : "border-brand-soft hover:shadow-lg"}`} data-testid={`vault-doc-${d.id}`}>
            <input type="checkbox" checked={selected.includes(d.id)} onChange={() => toggleSel(d.id)} data-testid={`vault-doc-select-${d.id}`} className="w-4 h-4 mt-0.5 accent-red-500 cursor-pointer shrink-0" />
            <span className="w-11 h-11 rounded-xl bg-brand-softer flex items-center justify-center text-brand-green shrink-0"><FileText className="w-5 h-5" /></span>
            <div className="flex-1 min-w-0">
              <a href={imgSrc(d.url)} target="_blank" rel="noreferrer" className="text-sm font-semibold text-brand-greenDeep truncate hover:text-brand-limeDark block">{d.label}</a>
              <p className="text-xs text-brand-green/50 truncate">{d.property_name}</p>
              <div className="flex items-center gap-1.5 mt-0.5">
                <span className="text-xs text-brand-green/50">Validité{d.expiry_auto ? " (déduite)" : ""} :</span>
                <input type="date" value={d.expiry_date ? d.expiry_date.slice(0, 10) : ""} onChange={(e) => setExpiry(d.id, e.target.value)} className="text-xs px-1.5 py-0.5 rounded-md border border-brand-soft bg-white text-brand-green outline-none focus:border-brand-lime" data-testid={`vault-doc-expiry-${d.id}`} />
              </div>
              <div className="flex items-center gap-1.5 mt-1.5">
                <span className="inline-block text-[10px] px-2 py-0.5 rounded-full bg-brand-softer text-brand-green font-semibold" data-testid={`vault-doc-cat-${d.id}`}>{d.category}{d.auto_classified ? " · auto" : ""}</span>
                <select value={RECLASS_CATS.includes(d.category) ? d.category : "Autre"} onChange={(e) => reclassify(d.id, e.target.value)} className="text-[10px] px-1.5 py-0.5 rounded-full border border-brand-soft bg-white text-brand-green outline-none focus:border-brand-lime cursor-pointer" title="Reclasser ce document" data-testid={`vault-doc-reclass-${d.id}`}>
                  {RECLASS_CATS.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
            </div>
            <div className="flex flex-col items-center gap-2 shrink-0">
              <a href={imgSrc(d.url)} target="_blank" rel="noreferrer" title="Télécharger"><Download className="w-4 h-4 text-brand-green/40 hover:text-brand-greenDeep" /></a>
              <button onClick={() => setConfirmDel(d.id)} title="Supprimer ce document" data-testid={`vault-doc-delete-${d.id}`} className="text-brand-green/40 hover:text-red-500 transition-colors"><Trash2 className="w-4 h-4" /></button>
            </div>
          </div>
        ))}
        {leases.length === 0 && docs.length === 0 && (
          <div className="col-span-full text-center py-16 text-brand-green/50">
            <FolderLock className="w-12 h-12 mx-auto mb-3 opacity-40" />
            Aucun document. Les baux signés et les diagnostics ajoutés apparaîtront ici.
          </div>
        )}
      </div>

      {confirmDel && (
        <div className="fixed inset-0 z-[70] bg-black/40 flex items-center justify-center p-4" onClick={() => !delBusy && setConfirmDel(null)}>
          <div className="bg-white rounded-3xl w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="vault-delete-modal">
            <div className="p-6 pb-4 border-b border-brand-soft flex items-center gap-3">
              <span className="w-11 h-11 rounded-full bg-red-50 flex items-center justify-center shrink-0"><Trash2 className="w-5 h-5 text-red-500" /></span>
              <h3 className="font-display text-xl font-semibold text-brand-greenDeep">{confirmDel === "bulk" ? `Supprimer ${selected.length} document(s) ?` : "Supprimer ce document ?"}</h3>
            </div>
            <div className="p-6 pt-5 space-y-4">
              <p className="text-sm text-brand-green/70">Le{confirmDel === "bulk" ? "s documents sélectionnés seront retirés" : " document sera retiré"} du coffre-fort. Cette action est définitive.</p>
              <div className="flex gap-3">
                <button onClick={() => setConfirmDel(null)} disabled={delBusy} className="flex-1 py-3 rounded-full border border-brand-soft text-brand-greenDeep font-semibold hover:bg-brand-softer transition-colors disabled:opacity-60" data-testid="vault-delete-cancel">Annuler</button>
                <button onClick={doDelete} disabled={delBusy} className="flex-1 py-3 rounded-full bg-red-500 hover:bg-red-600 text-white font-semibold flex items-center justify-center gap-2 transition-colors disabled:opacity-60" data-testid="vault-delete-confirm">
                  {delBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />} Supprimer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
